import { ref } from 'vue';
// Lightweight composable for reading commit history from the GitHub REST API.
// It deliberately does NOT use useApi/useAuth — those prepend the Syso backend
// base URL, whereas here we talk to api.github.com directly with our own token.
const REPO = 'tphillips/Syso';
const BRANCH = 'dev';
const TOKEN_KEY = 'gh-history-token';
const PER_PAGE = 100;
const MAX_PAGES = 30; // ~3000 commits per query — guards against runaway requests
export function useGitHistory() {
    const commits = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const truncated = ref(false);
    const authError = ref(false);
    const hasToken = ref(!!localStorage.getItem(TOKEN_KEY));
    function getToken() {
        return localStorage.getItem(TOKEN_KEY);
    }
    function setToken(token) {
        localStorage.setItem(TOKEN_KEY, token.trim());
        hasToken.value = true;
        authError.value = false;
    }
    function clearToken() {
        localStorage.removeItem(TOKEN_KEY);
        hasToken.value = false;
    }
    function mapCommit(raw) {
        const sha = raw.sha;
        const fullMessage = raw.commit?.message ?? '';
        return {
            sha,
            shortSha: sha.substring(0, 7),
            author: raw.commit?.author?.name ?? 'Unknown',
            login: raw.author?.login ?? null,
            avatar: raw.author?.avatar_url ?? null,
            date: raw.commit?.author?.date ?? '',
            message: fullMessage.split('\n')[0]
        };
    }
    // Paginated fetch for an optional sub-path (e.g. 'Sites', 'Services') on a
    // given branch (defaults to BRANCH). Sets the shared error/authError/truncated
    // flags unless `silent` — summary fetches pass silent so a feature-branch
    // hiccup can't blank the main page. Returns the commits it got.
    async function loadCommits(opts, path, branch = BRANCH, silent = false) {
        const token = getToken();
        if (!token) {
            authError.value = true;
            return [];
        }
        const collected = [];
        for (let page = 1; page <= MAX_PAGES; page++) {
            const params = new URLSearchParams({
                sha: branch,
                since: opts.since,
                until: opts.until,
                per_page: String(PER_PAGE),
                page: String(page)
            });
            if (path)
                params.set('path', path);
            const response = await fetch(`https://api.github.com/repos/${REPO}/commits?${params.toString()}`, {
                headers: {
                    Authorization: `bearer ${token}`,
                    Accept: 'application/vnd.github+json'
                }
            });
            if (response.status === 401) {
                authError.value = true;
                if (!silent)
                    error.value = 'GitHub rejected the token (401). Please enter a valid token.';
                return collected;
            }
            if (!response.ok) {
                if (!silent)
                    error.value = `GitHub API error (${response.status}): ${response.statusText}`;
                return collected;
            }
            const batch = await response.json();
            if (!Array.isArray(batch) || batch.length === 0) {
                break;
            }
            collected.push(...batch.map(mapCommit));
            if (batch.length < PER_PAGE) {
                break; // last page
            }
            if (page === MAX_PAGES && !silent) {
                truncated.value = true;
            }
        }
        return collected;
    }
    // Loads the whole repo into shared state for the main views.
    async function fetchCommits(opts) {
        loading.value = true;
        error.value = null;
        truncated.value = false;
        authError.value = false;
        try {
            commits.value = await loadCommits(opts);
        }
        catch (e) {
            error.value = e?.message || 'Failed to reach the GitHub API.';
        }
        finally {
            loading.value = false;
        }
    }
    // Loads commits touching a given sub-path; returns the array (no shared state).
    async function fetchCommitsForPath(opts, path) {
        try {
            return await loadCommits(opts, path);
        }
        catch {
            return [];
        }
    }
    // Loads commits across one or more branches for the AI summary, merged and
    // deduped by sha (a commit can appear on several branches), newest first.
    // Runs silent so it never disturbs the page's main commit-error state.
    async function fetchCommitsForBranches(opts, branches) {
        const lists = await Promise.all(branches.map(b => loadCommits(opts, undefined, b, /* silent */ true).catch(() => [])));
        const seen = new Set();
        const merged = [];
        for (const list of lists) {
            for (const c of list) {
                if (seen.has(c.sha))
                    continue;
                seen.add(c.sha);
                merged.push(c);
            }
        }
        merged.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
        return merged;
    }
    // Lists the repo's branch names (paginated). Used to populate the summary's
    // branch picker. When `activeSinceISO` is given, only branches whose HEAD
    // commit is on/after that instant are returned — GitHub exposes no branch
    // "creation" date (branches are just refs), so latest-commit recency is the
    // practical proxy for "recently worked on". Returns [] on any error.
    async function fetchBranches(activeSinceISO) {
        const token = getToken();
        if (!token)
            return [];
        const headers = {
            Authorization: `bearer ${token}`,
            Accept: 'application/vnd.github+json'
        };
        const raw = [];
        try {
            for (let page = 1; page <= 10; page++) {
                const params = new URLSearchParams({ per_page: String(PER_PAGE), page: String(page) });
                const response = await fetch(`https://api.github.com/repos/${REPO}/branches?${params.toString()}`, { headers });
                if (!response.ok)
                    break;
                const batch = await response.json();
                if (!Array.isArray(batch) || batch.length === 0)
                    break;
                raw.push(...batch.map((b) => ({ name: b.name, url: b.commit?.url })));
                if (batch.length < PER_PAGE)
                    break;
            }
        }
        catch {
            return raw.map(b => b.name);
        }
        if (!activeSinceISO)
            return raw.map(b => b.name);
        // Keep only branches whose HEAD commit lands on/after the cutoff. One
        // request per branch (the list response carries no date) — fine for the
        // occasional picker refresh; runs in parallel.
        const cutoff = new Date(activeSinceISO).getTime();
        const checked = await Promise.all(raw.map(async (b) => {
            if (!b.url)
                return null;
            try {
                const r = await fetch(b.url, { headers });
                if (!r.ok)
                    return null;
                const d = await r.json();
                const dateStr = d.commit?.committer?.date ?? d.commit?.author?.date;
                return dateStr && new Date(dateStr).getTime() >= cutoff ? b.name : null;
            }
            catch {
                return null;
            }
        }));
        return checked.filter((n) => !!n);
    }
    function mapRun(raw) {
        const sha = raw.head_sha ?? '';
        return {
            id: raw.id,
            name: raw.name ?? 'Workflow',
            status: raw.status ?? 'unknown',
            conclusion: raw.conclusion ?? null,
            headSha: sha,
            headShaShort: sha.substring(0, 7),
            event: raw.event ?? '',
            htmlUrl: raw.html_url ?? '',
            actor: raw.actor?.login ?? raw.triggering_actor?.login ?? null,
            createdAt: raw.created_at ?? '',
            updatedAt: raw.updated_at ?? ''
        };
    }
    // Latest workflow runs on BRANCH in a single request. Each run carries its
    // head_sha, so the page can both list runs and decorate commits without any
    // per-commit calls. Returns [] on auth/error (flags are not disturbed here —
    // the commit fetch owns the shared error state).
    async function fetchWorkflowRuns(perPage = 50) {
        const token = getToken();
        if (!token)
            return [];
        const params = new URLSearchParams({
            branch: BRANCH,
            per_page: String(perPage)
        });
        try {
            const response = await fetch(`https://api.github.com/repos/${REPO}/actions/runs?${params.toString()}`, {
                headers: {
                    Authorization: `bearer ${token}`,
                    Accept: 'application/vnd.github+json'
                }
            });
            if (!response.ok)
                return [];
            const data = await response.json();
            const runs = Array.isArray(data?.workflow_runs) ? data.workflow_runs : [];
            return runs.map(mapRun);
        }
        catch {
            return [];
        }
    }
    return {
        commits,
        loading,
        error,
        truncated,
        authError,
        hasToken,
        getToken,
        setToken,
        clearToken,
        fetchCommits,
        fetchCommitsForPath,
        fetchCommitsForBranches,
        fetchBranches,
        fetchWorkflowRuns,
        REPO,
        BRANCH
    };
}
